import { useCallback, useEffect, useState } from 'react';

export type TailwindBreakpoint = 'sm' | 'md' | 'lg' | 'xl' | '2xl';

const BREAKPOINTS: Record<TailwindBreakpoint, number> = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  '2xl': 1536,
};

const BREAKPOINT_ORDER = Object.keys(BREAKPOINTS) as TailwindBreakpoint[];

const getWidth = (): number =>
  typeof window !== 'undefined' ? window.innerWidth : 0;

const resolveBreakpoint = (width: number): TailwindBreakpoint => {
  const active = [...BREAKPOINT_ORDER]
    .reverse()
    .find((bp) => width >= BREAKPOINTS[bp]);

  return active ?? 'sm';
};

export type BreakpointState = {
  breakpoint: TailwindBreakpoint;
  width: number;
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  isAbove: (bp: TailwindBreakpoint) => boolean;
  isBelow: (bp: TailwindBreakpoint) => boolean;
};

export const useBreakpoint = (): BreakpointState => {
  const [width, setWidth] = useState<number>(getWidth);

  useEffect(() => {
    const handleResize = () => setWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isAbove = useCallback(
    (bp: TailwindBreakpoint) => width >= BREAKPOINTS[bp],
    [width],
  );

  const isBelow = useCallback(
    (bp: TailwindBreakpoint) => width < BREAKPOINTS[bp],
    [width],
  );

  return {
    breakpoint: resolveBreakpoint(width),
    width,
    isMobile: width < BREAKPOINTS.md,
    isTablet: width >= BREAKPOINTS.md && width < BREAKPOINTS.lg,
    isDesktop: width >= BREAKPOINTS.lg,
    isAbove,
    isBelow,
  };
};
